import type { GridRowId } from '@mui/x-data-grid-premium';
import type { GridApiPremium } from '@mui/x-data-grid-premium/models/gridApiPremium';

type NavApi = Pick<GridApiPremium, 'getSortedRowIds' | 'startCellEditMode' | 'scrollToIndexes'>;

/**
 * WA-3633 — Enter-advance for the produced-quantity grid, screen-local and
 * race-free by construction.
 *
 * Called from the grid's onCellKeyDown AFTER the shared Table has already
 * stopped+committed the current cell (MUI's stop schedules the commit —
 * processRowUpdate then editor unmount — on a microtask). Starting the next
 * row's edit is deferred to a MACROTASK (setTimeout 0) so it runs strictly
 * after that microtask has drained and the previous editor has unmounted: at
 * that instant no cell is in edit mode, so the next edit opens cleanly and
 * exactly one editor is ever mounted. This is the boundary the four prior
 * fixes lacked.
 *
 * On the last sorted row there is no next row, so Enter just commits (no wrap).
 * If the current row is no longer in the sorted set (filtered out or removed
 * after the commit), there is nothing to advance from — bail out rather than
 * fall through to index 0, which would wrongly jump focus to the first row.
 * scrollToIndexes always receives the numeric ROW INDEX — never
 * Number(nextRowId), which is NaN for GridRowId/UUID row ids (the c2a14d26
 * regression).
 */
export const advanceToNextProducedQuantityRow = (
  api: NavApi,
  id: GridRowId,
  field: string,
): void => {
  const sortedRowIds = api.getSortedRowIds();
  const currentRowIndex = sortedRowIds.indexOf(id);
  if (currentRowIndex === -1) {
    return;
  }

  const nextRowIndex = currentRowIndex + 1;
  const nextRowId = sortedRowIds[nextRowIndex];
  if (nextRowId === undefined) {
    return;
  }

  setTimeout(() => {
    api.startCellEditMode({ id: nextRowId, field });
    api.scrollToIndexes({ rowIndex: nextRowIndex });
  }, 0);
};
